import type { WallOutline } from "@/widgets/plan";

const HATCH_COLOR = "#111111";
const PATTERN_SCALE = 25;
const LINE_WIDTH = 12;

export interface WallHatchProps {
	outline: WallOutline;
}

const rotatedBounds = (outline: WallOutline, angle: number) => {
	const radians = (-angle * Math.PI) / 180;
	const cosine = Math.cos(radians);
	const sine = Math.sin(radians);
	return outline.reduce(
		(bounds, point) => {
			const x = point.x * cosine - point.y * sine;
			const y = point.x * sine + point.y * cosine;
			return {
				minX: Math.min(bounds.minX, x),
				maxX: Math.max(bounds.maxX, x),
				minY: Math.min(bounds.minY, y),
				maxY: Math.max(bounds.maxY, y),
			};
		},
		{ minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity },
	);
};

const gridStart = (value: number, step: number) => Math.floor(value / step) * step;
const gridEnd = (value: number, step: number) => Math.ceil(value / step) * step;

export const LineHatch = ({
	outline,
	angle,
	spacing,
}: WallHatchProps & { angle: number; spacing: number }) => {
	const step = spacing * PATTERN_SCALE;
	const bounds = rotatedBounds(outline, angle);
	const minX = gridStart(bounds.minX, step);
	const maxX = gridEnd(bounds.maxX, step);
	const minY = gridStart(bounds.minY, step);
	const maxY = gridEnd(bounds.maxY, step);
	const lines: string[] = [];
	for (let x = minX; x <= maxX; x += step)
		lines.push(`M ${x} ${minY - step} L ${x} ${maxY + step}`);

	return (
		<g transform={angle === 0 ? undefined : `rotate(${angle})`}>
			<path d={lines.join(" ")} fill="none" stroke={HATCH_COLOR} strokeWidth={LINE_WIDTH} />
		</g>
	);
};

export const DotHatch = ({ outline, spacing }: WallHatchProps & { spacing: number }) => {
	const step = spacing * PATTERN_SCALE;
	const bounds = rotatedBounds(outline, 0);
	const dots: { x: number; y: number }[] = [];
	for (let x = gridStart(bounds.minX, step); x <= gridEnd(bounds.maxX, step); x += step) {
		for (let y = gridStart(bounds.minY, step); y <= gridEnd(bounds.maxY, step); y += step) {
			dots.push({ x: x + step / 2, y: y + step / 2 });
		}
	}

	return (
		<>
			{dots.map((dot) => (
				<circle key={`${dot.x}-${dot.y}`} cx={dot.x} cy={dot.y} r={LINE_WIDTH} fill={HATCH_COLOR} />
			))}
		</>
	);
};
